import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

// Only DateTimeUtils should ever import this class directly!
// There is NO backward compatibility support for this module.

import { Assert } from "abweb/utils/Assert";
import { AbInitioCustomFormat, isDateTimeParser } from "abweb/utils/date/DateTimeStructs";
import { DateTimeUtils } from "abweb/utils/date/DateTimeUtils";
import { DateUtilsLuxonInternals } from "abweb/utils/date/internal/DateUtilsLuxonInternals";
import { EnumUtils } from "abweb/utils/EnumUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
const LOG = LogUtils.getLogger(__filename);

// Do NOT directly use this class - instead, use DateTimeUtils methods that act as a facade for
// the public methods in this class
export class DateFormatInternal {
  constructor() {
    // static helper class
  }

  ///////////  Internal State ///////////////////

  // When possible, use Intl-backed formatter and parser, as then UI stays consistent for localized date/times

  // No resetStatics as DateTimeUtils.resetStatics invokes DTU.onLocaleChange, which invokes below onLocaleChange

  /**
   * Sets the current locale for formatting.
   * Invoked directly by DateTimeUtils.onLocaleChange after the DTU has updated to the newDatetimeLocale.
   */
  static onLocaleChange(newDatetimeLocale) {
    LOG.info(`DateFormatInternal.datetimeLocale is '${newDatetimeLocale}'`);
    DateFormatInternal.resetKendoFormatters();
  }
  static resetKendoFormatters() {
    // Calc current DateTimeFormatter now that DateTimeUtils has been updated to newDatetimeLocale
    DateFormatInternal.FormatterToKendoParseFormat = new Map();
    DateFormatInternal.AbInitioCustomFormatters.forEach((dtfFn, fmt) => {
      const dtf = dtfFn();
      // Kendo parser doesn't support parsing the DATE{TIME}_LONG as they have the day of week in them
      // However, Luxon does, so can't use: if (isDateTimeParser(dtf))...
      if (isDateTimeParser(dtf) && ![AbInitioCustomFormat.DATE_LONG, AbInitioCustomFormat.DATETIME_LONG].includes(fmt)) {
        const kendoParseFormat = DateUtilsLuxonInternals.convertLuxonDateTimeParseFormatToKendoParseFormat(dtf);
        DateFormatInternal.FormatterToKendoParseFormat.set(dtf.format, kendoParseFormat);
      }
    });
  }
  static getCustomAbInitioFormatter(abInitioFormat) {
    if (EnumUtils.enumValueByValue(AbInitioCustomFormat, abInitioFormat)) {
      const dtfFn = DateFormatInternal.AbInitioCustomFormatters.get(abInitioFormat);
      Assert.assert(dtfFn != null, `Formatter not found for AbInitioCustomFormat value: '${abInitioFormat}'`);
      return dtfFn();
    }
  }

  ////// Public Formatter Apis /////////
  /**
   * Formats a Date according to the specified KENDO format (or custom ab initio format, such as ab:relative_date)
   * and the current kendo culture.
   * It supports the full kendo date formatting syntax, but for the commonly occurring locale sensitive Date or DateTime formatting,
   * please use the formatDateValue and formatDateTimeValue methods (slightly more efficient, and
   * better decoupling from underlying kendo locale format strings).
   *
   * @param value Date object.  If null, empty string is returned.
   * @param abInitioFormat {AbInitioDateFormat} Either a prebuilt AbInitioCustomDateFormat string,
   * or a Luxon format string.
   * @param enableKendoSupport {boolean} if true, will assume that the abInitioFormat is a kendo format string
   * if it isn't a AbInitioCustomDateFormat string. It will internally convert it to a luxon format string.
   * Note that kendo format is deprecated, so it is here for backward compatibility only.
   */
  static formatValue(value, abInitioFormat, enableKendoSupport = false) {
    // Historically kendoFormatters return empty string if value was null or undefined.
    // Now that this is backed by DateTimeUtils formatters, they throw if passed null or undefined,
    // so explicitly handle null and undefined for backward compatibility
    if (value == null) return "";
    const dtFormatter = DateFormatInternal.buildAbInitioDateTimeFormatter(abInitioFormat, enableKendoSupport);
    return dtFormatter.format(value);
  }

  /**
   * @param abInitioFormat {AbInitioDateFormat} Either a prebuilt AbInitioCustomDateFormat string,
   * or a Luxon format string.
   * @param enableKendoSupport {boolean} if true, will assume the abInitioFormat is a kendo format string,
   * and converts it to the equivalent luxon format string.
   */
  static buildAbInitioDateTimeFormatter(abInitioFormat, enableKendoSupport = false) {
    const dtf = DateFormatInternal.getCustomAbInitioFormatter(abInitioFormat);
    if (dtf) {
      return dtf;
    }

    // formerly formated via: kendo.toString(val, this.kendoFormat)
    const luxonFormat = enableKendoSupport ? DateUtilsLuxonInternals.convertToLuxonFormat(abInitioFormat) : abInitioFormat;
    return DateTimeUtils.buildLuxonDateTimeFormatter(luxonFormat);
  }

  /**
   * @param abInitioFormat {AbInitioDateFormat} Either a prebuilt AbInitioCustomDateFormat string,
   * or a Luxon format string.  The luxon format string must be valid for both formatting and parsing.
   * @param enableKendoSupport {boolean} if true, will assume the abInitioFormat is a kendo format string,
   * and converts it to the equivalent luxon format string.   */
  static buildAbInitioDateTimeFormatterAndParser(abInitioFormat, enableKendoSupport = false) {
    const dtf = DateFormatInternal.getCustomAbInitioFormatter(abInitioFormat);
    if (dtf) {
      if (isDateTimeParser(dtf)) {
        return dtf;
      }
      throw new Error(`The registered CustomAbInitioFormatter for '${abInitioFormat}' does not support the DateTimeParser contract.`);
    }

    // formerly formated via: kendo.toString(val, this.kendoFormat)
    const luxonFormat = enableKendoSupport ? DateUtilsLuxonInternals.convertToLuxonFormat(abInitioFormat) : abInitioFormat;

    // Assumes the luxonFormat is valid for both formatting and parsing (which is mostly true)!
    return DateTimeUtils.buildLuxonDateTimeFormatterAndParser(luxonFormat);
  }
}
// No DateFormatInternal.resetStatics() - see onLocaleChange comment
_defineProperty(DateFormatInternal, "AbInitioCustomFormatters", new Map([[AbInitioCustomFormat.DATE_SHORT, () => DateTimeUtils.DATE_SHORT],
// Currently do not have builtin-parser for DATE_LONG. DATETIME_LONG (both have day of week)
[AbInitioCustomFormat.TIME_WITH_MINUTES, () => DateTimeUtils.TIME_WITH_MINUTES], [AbInitioCustomFormat.TIME_WITH_SECONDS, () => DateTimeUtils.TIME_WITH_SECONDS], [AbInitioCustomFormat.DATETIME_WITH_MINUTES, () => DateTimeUtils.DATETIME_WITH_MINUTES], [AbInitioCustomFormat.DATETIME_WITH_SECONDS, () => DateTimeUtils.DATETIME_WITH_SECONDS],
// Currently do not have builtin-parser for DATE_LONG, DATETIME_LONG (both have day of week)
[AbInitioCustomFormat.DATE_LONG, () => DateTimeUtils.DATE_LONG], [AbInitioCustomFormat.DATETIME_LONG, () => DateTimeUtils.DATETIME_LONG],
// Ab Initio custom formats
[AbInitioCustomFormat.MONTH_AND_DAY, () => DateTimeUtils.DATE_MONTH_AND_DAY], [AbInitioCustomFormat.DATE_RELATIVE, () => DateTimeUtils.FORMAT_DATE_RELATIVE], [AbInitioCustomFormat.DATETIME_RELATIVE, () => DateTimeUtils.FORMAT_DATETIME_RELATIVE], [AbInitioCustomFormat.DATETIME_RELATIVE_EX, () => ({
  // default is to NOT includeSeconds
  format: val => DateTimeUtils.formatRelativeDateTimeEx(val, false)
})]]));
////////////////////   Start Legacy Kendo Format Support //////////////////////////////
// Used by (native) kendo widgets that take the kendo parse format string (so can NOT contain non-kendo format strings)
_defineProperty(DateFormatInternal, "FormatterToKendoParseFormat", void 0);
//# sourceMappingURL=DateFormatInternal.js.map
